import type { IndexedStorySection } from '@prezly/theme-kit-core';
import classNames from 'classnames';
import Link from 'next/link';
import type { Hit } from 'react-instantsearch-core';
import { Highlight, Snippet } from 'react-instantsearch-dom';

import { CategoriesList, StoryImage, StoryPublicationDate } from '@/components';
import { FEATURED_CATEGORY_ID } from '@/constants';
import { useThemeSettings } from '@/hooks';
import { slugifyHeading } from '@/utils';

import { useSearchState } from './SearchStateContext';

import styles from './Hit.module.scss';
import cardStyles from '@/components/StoryCards/StoryCard.module.scss';

interface Props {
    hit: Hit<{ attributes: IndexedStorySection }>;
}

// This is mostly a copy of `StoryCard` component, but since the data structure is a bit different,
// it requires a separate component for indexed content
function HitComponent({ hit }: Props) {
    const { attributes: story } = hit;
    const categories = story.categories.filter((c) => c.id !== FEATURED_CATEGORY_ID);
    const { showDate, showSubtitle } = useThemeSettings();
    const { searchState } = useSearchState();

    const sectionHeading = story.section_title ?? story.section_subtitle;
    const sectionHeadingAttribute = story.section_title
        ? 'attributes.section_title'
        : 'attributes.section_subtitle';
    const hash = sectionHeading ? `#header-${slugifyHeading(sectionHeading)}` : '';

    const hasQuery = Boolean(searchState.query?.trim());
    const hasContentSnippet = Boolean(story.content_text);
    const showSectionSnippet = hasQuery && (Boolean(sectionHeading) || hasContentSnippet);

    return (
        <div className={classNames(cardStyles.container, cardStyles.small)}>
            <Link href={`/${story.slug}${hash}`} locale={false} className={cardStyles.imageWrapper}>
                <StoryImage
                    story={story}
                    className={cardStyles.image}
                    placeholderClassName={cardStyles.placeholder}
                />
            </Link>
            <div className={cardStyles.content}>
                {categories.length > 0 && (
                    <div className={cardStyles.categories}>
                        <CategoriesList categories={categories} isStatic />
                    </div>
                )}
                <h3 className={classNames(cardStyles.title, cardStyles.titleSmaller)}>
                    <Link
                        href={`/${story.slug}${hash}`}
                        locale={false}
                        className={classNames(cardStyles.titleLink, styles.title)}
                    >
                        <Highlight hit={hit} attribute="attributes.title" tagName="mark" />
                    </Link>
                </h3>

                {(showSubtitle || showSectionSnippet) && (
                    <p className={cardStyles.subtitle}>
                        {showSectionSnippet ? (
                            <span className={styles.snippet}>
                                {sectionHeading && (
                                    <span className={styles.section}>
                                        <Highlight
                                            hit={hit}
                                            attribute={sectionHeadingAttribute}
                                            tagName="mark"
                                        />
                                    </span>
                                )}
                                {sectionHeading && hasContentSnippet && (
                                    <span className={styles.separator}> · </span>
                                )}
                                {hasContentSnippet && (
                                    <Snippet
                                        hit={hit}
                                        attribute="attributes.content_text"
                                        tagName="mark"
                                    />
                                )}
                            </span>
                        ) : (
                            <Link
                                href={`/${story.slug}${hash}`}
                                locale={false}
                                className={cardStyles.titleLink}
                            >
                                {story.subtitle}
                            </Link>
                        )}
                    </p>
                )}

                {showDate && (
                    <p className={cardStyles.date}>
                        <StoryPublicationDate story={story} />
                    </p>
                )}
            </div>
        </div>
    );
}

export default HitComponent;
